import React, { useEffect, useState } from 'react';

const LIKERT_SHORT = {
  1: 'Inte alls',
  2: 'Delvis inte',
  3: 'Delvis',
  4: 'Helt',
};

const percent = (count, total) =>
  total > 0 ? Math.round((100 * count) / total) : 0;

const BarRow = ({ label, value, max }) => (
  <div className="mb-2">
    <div className="flex justify-between text-sm mb-1">
      <span>{label}</span>
      <span className="tabular-nums">{value}%</span>
    </div>
    <div className="h-2 bg-gray-100 rounded overflow-hidden">
      <div
        className="h-full bg-black rounded"
        style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }}
      />
    </div>
  </div>
);

const CompassPoll = ({
  compassId,
  resorts,
  questions,
  stats,
  pollError,
  labels,
}) => {
  const [openQuestion, setOpenQuestion] = useState(null);

  useEffect(() => {
    if (questions?.length && openQuestion == null) {
      setOpenQuestion(questions[0]?.question_key || null);
    }
  }, [questions, openQuestion]);

  if (pollError) {
    return (
      <div className="col-span-12 mt-8">
        <h3 className="mb-2">{labels?.pollHeading || 'Så här svarade andra'}</h3>
        <p className="text-sm opacity-70">
          {labels?.pollError || 'Kunde inte hämta omröstningen just nu.'}
        </p>
      </div>
    );
  }

  if (!stats || !stats.responses_total) {
    return (
      <div className="col-span-12 mt-8">
        <h3 className="mb-2">{labels?.pollHeading || 'Så här svarade andra'}</h3>
        <p className="text-sm opacity-70">
          {labels?.pollEmpty || 'Du är först — fler resultat visas när fler svarat.'}
        </p>
      </div>
    );
  }

  const total = stats.responses_total;
  const resortTitleByKey = new Map(
    (resorts || []).map((r) => [r.resort_key, r.title || r.resort_key]),
  );

  const matchRows = Object.entries(stats.top_match || {})
    .map(([key, count]) => ({
      key,
      label: resortTitleByKey.get(key) || key,
      count: Number(count) || 0,
      pct: percent(Number(count) || 0, total),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const maxMatchPct = Math.max(...matchRows.map((r) => r.pct), 1);

  return (
    <div className="col-span-12 mt-10 grid gap-8">
      <div>
        <h3 className="mb-1">{labels?.pollHeading || 'Så här svarade andra'}</h3>
        <p className="text-sm opacity-70 mb-4">
          {total} {total === 1 ? 'svar' : 'svar'}
        </p>

        <h4 className="mb-3">{labels?.pollMatchHeading || 'Mest matchade orter'}</h4>
        {matchRows.map((row) => (
          <BarRow
            key={row.key}
            label={row.label}
            value={row.pct}
            max={maxMatchPct}
          />
        ))}
      </div>

      <div>
        <h4 className="mb-3">
          {labels?.pollQuestionsHeading || 'Svar per påstående'}
        </h4>
        <div className="grid gap-2">
          {(questions || []).map((question) => {
            const qKey = question.question_key;
            const isOpen = openQuestion === qKey;
            const bucket = stats.questions?.[qKey] || {
              1: 0,
              2: 0,
              3: 0,
              4: 0,
              skipped: 0,
            };
            const qTotal =
              Number(bucket[1] || 0) +
              Number(bucket[2] || 0) +
              Number(bucket[3] || 0) +
              Number(bucket[4] || 0) +
              Number(bucket.skipped || 0);

            return (
              <div key={qKey} className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  className="w-full text-left px-4 py-3 flex justify-between gap-2"
                  onClick={() => setOpenQuestion(isOpen ? null : qKey)}
                  aria-expanded={isOpen}
                >
                  <span>{question.title || qKey}</span>
                  <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen ? (
                  <div className="px-4 pb-4">
                    {[1, 2, 3, 4].map((value) => (
                      <BarRow
                        key={value}
                        label={LIKERT_SHORT[value]}
                        value={percent(Number(bucket[value] || 0), qTotal)}
                        max={100}
                      />
                    ))}
                    {Number(bucket.skipped || 0) > 0 ? (
                      <BarRow
                        label="Hoppat över"
                        value={percent(Number(bucket.skipped || 0), qTotal)}
                        max={100}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CompassPoll;
